import filter from "lodash/filter";
import head from "lodash/head";
import isEmpty from "lodash/isEmpty";
import last from "lodash/last";
import orderBy from "lodash/orderBy";
import reduce from "lodash/reduce";
import some from "lodash/some";
import { Warnings } from "PFApp/booking/types";
import { utc } from "PFCore/helpers/date";
import { getProfileName } from "PFCore/helpers/profile";
import { roundToDecimals } from "PFCore/helpers/rounding";
import { ProfileWarningsIndex } from "PFCore/hooks/queries/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import {
  Activity,
  AuditPlannerException,
  AuditPlannerExceptionsDictionary,
  AuditRole,
  BasicProfile,
  Shortlist
} from "PFTypes";
import { useTranslation } from "react-i18next";
import { humanize, titleize } from "underscore.string";

import { EnhancedAuditRole } from "../table/audit_planner_table.types";

export const getShortlistItem = (role: EnhancedAuditRole) =>
  role.shortlists.find(
    ({ state, exceptions }) =>
      ["filled", "booked", "confirmed"].includes(state) && some(exceptions, { state: "pending" })
  );

export const makeExceptionTypesUserFriendly = (exceptionTypes: string[]) =>
  exceptionTypes.map((type) => titleize(humanize(type)));

export const getExceptionTypes = (exception: AuditPlannerExceptionsDictionary) => {
  const isMultiple = exception.availability && exception.compliance;

  if (isMultiple) {
    const complianceTypes = Object.keys(exception.compliance || {});
    const availabilityTypes = Object.keys(exception.availability || {});
    return makeExceptionTypesUserFriendly([...complianceTypes, ...availabilityTypes]);
  }

  const exceptionTypes = Object.keys(Object.values(exception)[0] || {});
  return makeExceptionTypesUserFriendly(exceptionTypes);
};

export const getExceptionType = (exception: AuditPlannerExceptionsDictionary) => {
  const isMultiple = exception.availability && exception.compliance;

  return isMultiple
    ? "Multiple"
    : makeExceptionTypesUserFriendly(Object.keys(Object.values(exception)[0] || {})).join(", ");
};

export const exceptionsArrayToException = (auditPlannerExceptions: AuditPlannerException[]) => {
  const groupedExceptions = reduce(
    auditPlannerExceptions,
    (acc, curr) => {
      const { exceptionType, exceptions } = curr;
      const newAcc = { ...acc };
      if (newAcc[exceptionType]) {
        newAcc[exceptionType] = { ...newAcc[exceptionType], ...exceptions };
      } else {
        newAcc[exceptionType] = exceptions;
      }
      return newAcc;
    },
    {}
  );

  const exceptions = orderBy(auditPlannerExceptions, ["id"], ["desc"]); // sort newest first to search for requester, approver and reason in the proper order

  const requester =
    exceptions.find(({ requester }) => requester)?.requester ||
    ((exceptions.find(({ requesterId }) => requesterId) && {
      id: exceptions.find(({ requesterId }) => requesterId)?.requesterId
    }) as BasicProfile);

  const approver =
    exceptions.find(({ approver }) => approver)?.approver ||
    ((exceptions.find(({ approverId }) => approverId) && {
      id: exceptions.find(({ approverId }) => approverId)?.approverId
    }) as BasicProfile);

  return {
    exceptions: groupedExceptions,
    requester,
    approver,
    requestReason: exceptions.find(({ requestReason }) => requestReason)?.requestReason,
    state: exceptions.find(({ state }) => state)?.state
  };
};

export const getExceptionFromShortlist = (
  shortlist: Shortlist,
  exceptionState: AuditPlannerException["state"]
) => {
  const exceptions = filter(shortlist.exceptions || [], { state: exceptionState });
  return exceptions.length > 0 ? exceptionsArrayToException(exceptions) : null;
};

export const getExceptionFromRole = (role: AuditRole) => {
  const exceptions = role.shortlists?.find(
    ({ state, exceptions }) =>
      ["filled", "booked", "confirmed"].includes(state) && some(exceptions, { state: "pending" })
  )?.exceptions;
  return exceptions && exceptions.length > 0
    ? exceptionsArrayToException(filter(exceptions, { state: "pending" }))
    : null;
};

export const isAuditRoleDisabled = (role: AuditRole | Activity) =>
  ["pending", "auto_filling", "auto_shortlisting"].includes(
    (role as AuditRole).workflowState || (role as Activity).workflow_state
  );

export const findFilledOrBookedProfile = (auditRole: AuditRole): Shortlist["profile"] | undefined =>
  auditRole.shortlists?.find(({ state }) => ["filled", "booked"].includes(state))?.profile;

export const isOverbooked = (role: AuditRole, rolesWarningsByProfile: ProfileWarningsIndex) => {
  const profile = findFilledOrBookedProfile(role);
  const { overbookings } = profile ? (rolesWarningsByProfile[profile.id] ?? {}) : {};
  return !isEmpty(overbookings);
};

export const useOverbookingMessage = (role: AuditRole, warnings: Warnings | null) => {
  const { formatDate } = useDateFormatter();
  const { t } = useTranslation("auditPlanner");

  const profile =
    role.shortlists && role.shortlists.find(({ state }) => ["filled", "booked"].includes(state))?.profile;
  const { overbookings = [] } = warnings ?? {};

  if (!profile || isEmpty(overbookings)) {
    return null;
  }

  const overbookedMinutes = overbookings
    .filter(({ availableMinutes }) => availableMinutes < 0)
    .map(({ availableMinutes }) => Math.abs(availableMinutes))
    .reduce((sum, value) => sum + value, 0);

  const { date: startDate } = head(overbookings)!;
  const { date: endDate } = last(overbookings)!;

  return t("parts.userIsOverbooked", {
    name: getProfileName(profile),
    hours: roundToDecimals(overbookedMinutes / 60),
    startDate: formatDate(utc(startDate)),
    endDate: formatDate(utc(endDate))
  });
};
